// The paper's words: what each listing is called, figures as a reader says them, and the kicker,
// headline and dek of every story, written from what was measured. The outlets' own headlines are
// quoted as theirs; everything else here is a description of data, never a claim about causes
// beyond "likely".

import type { AssetClass } from "../../shared/newsroom/types.ts";
import type { Confidence, Move, Story } from "./engine";
import { CLASS_LABEL, type Market } from "./universe";

/** Listings whose ticker says less than a name would. Pairs say which way they are quoted. */
const NAMES: Record<string, string> = {
  SP500: "S&P 500",
  XYZ100: "Nasdaq 100",
  JP225: "Nikkei 225",
  US500: "S&P 500",
  USTECH: "Nasdaq 100",
  SMALL2000: "Russell 2000",
  USBOND: "US bonds",
  GOLD: "Gold",
  SILVER: "Silver",
  CL: "WTI crude",
  BRENTOIL: "Brent crude",
  NATGAS: "Natural gas",
  COPPER: "Copper",
  PLATINUM: "Platinum",
  PALLADIUM: "Palladium",
  URANIUM: "Uranium",
  ALUMINIUM: "Aluminium",
  EUR: "EUR/USD",
  GBP: "GBP/USD",
  JPY: "USD/JPY",
  KRW: "USD/KRW",
  CNH: "USD/CNH",
  CHF: "USD/CHF",
  DXY: "Dollar index",
  VIX: "VIX",
  "10Y": "US 10-year yield",
  "2Y": "US 2-year yield",
  "30Y": "US 30-year yield",
  BTCD: "Bitcoin dominance",
  TOTAL2: "Crypto ex-BTC",
  BTC: "Bitcoin",
  ETH: "Ether",
  SOL: "Solana",
  HYPE: "Hyperliquid",
  XRP: "XRP",
  DOGE: "Dogecoin",
};

/** What a chip or a chart label says: short, and what traders call it. */
const SHORT: Record<string, string> = {
  SP500: "S&P",
  US500: "S&P",
  XYZ100: "NDX",
  USTECH: "NDX",
  JP225: "Nikkei",
  SMALL2000: "RUT",
  GOLD: "Gold",
  SILVER: "Silver",
  CL: "WTI",
  BRENTOIL: "Brent",
  NATGAS: "NatGas",
  COPPER: "Copper",
  PLATINUM: "Platinum",
  EUR: "EURUSD",
  GBP: "GBPUSD",
  JPY: "USDJPY",
  KRW: "USDKRW",
  CNH: "USDCNH",
  "10Y": "US10Y",
  "2Y": "US2Y",
  "30Y": "US30Y",
};

export const shortOf = (symbol: string): string => SHORT[symbol] ?? symbol;

export function nameOf(market: Market): string {
  return NAMES[market.symbol] ?? market.symbol;
}

/** The ticker as traders write it, with the dex when it is not the main one. */
export function tickerOf(market: Market): string {
  return market.dex ? `${market.symbol} · ${market.dex}` : market.symbol;
}

const MINUS = "−";

/** A fractional return as a signed percentage: `+1.24%`, `−0.55%`. */
export function pct(ret: number, places = ret !== 0 && Math.abs(ret) < 0.01 ? 2 : Math.abs(ret) < 0.1 ? 2 : 1): string {
  const text = (Math.abs(ret) * 100).toFixed(places);
  if (Number(text) === 0) return `0.${"0".repeat(places)}%`;
  return `${ret < 0 ? MINUS : "+"}${text}%`;
}

/** A price as Hyperliquid would quote it: up to five significant figures, at most the listing's
 * decimals, grouped. For display only. */
export function px(value: number, market: Pick<Market, "pxDecimals">): string {
  if (!Number.isFinite(value)) return "—";
  const magnitude = Math.floor(Math.log10(Math.abs(value) || 1)) + 1;
  const places = Math.max(0, Math.min(market.pxDecimals, 5 - magnitude));
  return value.toLocaleString("en-US", { minimumFractionDigits: places, maximumFractionDigits: places });
}

/** `$1.2B`, `$310M`, `$45K`. */
export function money(value: number): string {
  const abs = Math.abs(value);
  const [div, unit] = abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : abs >= 1e3 ? [1e3, "K"] : [1, ""];
  const shown = abs / div;
  return `$${shown.toFixed(shown >= 100 || !unit ? 0 : 1)}${unit}`;
}

export function duration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours} hour${hours === 1 ? "" : "s"}` : `${hours.toFixed(1)} hours`;
}

/** `14:30` local. */
export function clockOf(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** `14:30 UTC`. */
export function utcOf(ms: number): string {
  return `${new Date(ms).toISOString().slice(11, 16)} UTC`;
}

/** The reader's time zone as they would say it: `CEST`, `GMT+4`. */
export function zoneOf(ms = Date.now()): string {
  const part = new Intl.DateTimeFormat("en-GB", { timeZoneName: "short" }).formatToParts(new Date(ms)).find((p) => p.type === "timeZoneName");
  return part?.value ?? "local";
}

export function ago(ms: number, now = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - ms) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
}

/** How many times the usual move: `14×`. */
export const times = (z: number) => `${Math.round(Math.abs(z))}×`;

const verb = (move: Move, present = false) =>
  move.ret >= 0 ? (present ? "rises" : "rose") : present ? "falls" : "fell";

const CONFIDENCE_WORDS: Record<Confidence, string> = {
  strong: "Strong fit",
  likely: "Likely cause",
  possible: "Possible cause",
  none: "No cause found",
};
export const confidenceWords = (c: Confidence) => CONFIDENCE_WORDS[c];

export type Copy = {
  /** The line above the headline: what desk and when. */
  kicker: string;
  /** The outlet's headline when a cause fits; a plain description of the move otherwise. */
  headline: string;
  /** Whose headline it is, when it is someone's. */
  outlet: string | null;
  link: string | null;
  /** One sentence of what was measured. */
  dek: string;
};

function desk(story: Story): string {
  if (story.classes.length > 2) return "Cross-asset";
  if (story.classes.length === 2) return story.classes.map((c: AssetClass) => CLASS_LABEL[c]).join(" & ");
  return CLASS_LABEL[story.class];
}

const CALENDAR: [string, string][] = [
  ["cal:open:", "at the New York open"],
  ["cal:close:", "into the New York close"],
  ["cal:data:", "as US data lands at 8:30 New York"],
];

export function copyOf(story: Story, byCoin: Map<string, Market>): Copy {
  const lead = byCoin.get(story.lead.coin);
  const leadName = lead ? nameOf(lead) : story.lead.coin;
  const move = story.lead;
  const span = duration(move.end - move.start);
  const kicker = `${desk(story)} · ${clockOf(move.start)}${move.ongoing ? " · moving now" : ""}`;
  const others = story.moves.slice(1);
  const otherWords = others
    .slice(0, 4)
    .map((m) => {
      const market = byCoin.get(m.coin);
      return `${market ? nameOf(market) : m.coin} ${pct(m.ret)}`;
    })
    .join(", ");
  const tail = others.length > 4 ? ` and ${others.length - 4} more` : "";
  const dek =
    `${leadName} ${verb(move)} ${pct(move.ret).replace(/^[+−]/, "")} in ${span}, ${times(move.z)} its usual move` +
    (move.notional >= 1e6 ? ` on ${money(move.notional)} traded.` : ".") +
    (others.length ? ` With it: ${otherWords}${tail}.` : "");
  const cause = story.confidence === "none" ? undefined : story.causes[0];
  // the calendar is not an outlet: a move that only the clock explains is written as one
  const when = cause ? CALENDAR.find(([prefix]) => cause.item.id.startsWith(prefix))?.[1] : undefined;
  if (when) {
    const headline =
      story.classes.length > 1
        ? `${story.markets.slice(0, 3).map(nameOf).join(", ")} move together ${when}`
        : `${leadName} ${verb(move, true)} ${pct(move.ret).replace(/^[+−]/, "")} ${when}`;
    return { kicker, headline, outlet: null, link: null, dek };
  }
  if (cause) {
    return { kicker, headline: cause.item.title, outlet: cause.item.source, link: cause.item.link, dek };
  }
  const headline =
    story.classes.length > 1
      ? `Markets lurch at ${clockOf(move.start)}: ${story.markets.slice(0, 3).map(nameOf).join(", ")} move together, nothing on the wire`
      : `${leadName} ${verb(move, true)} ${pct(move.ret).replace(/^[+−]/, "")} in ${span} with nothing on the wire`;
  return { kicker, headline, outlet: null, link: null, dek };
}
